import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { notifyError, notifySuccess, notifyInfo } from '../../../utils/notify'
import {
  detectBarcodesFromCanvas,
  extractLinhaDigitavel,
  extractPixCopiaECola,
  getPdfPlainText,
  getSelectedOrRegionText,
  pickLinhaFromStrings,
  pickPixFromStrings,
  renderPdfPageToCanvas,
} from './pdfCapture'
import './PdfPreviewPanel.css'

const ZOOM_MIN = 50
const ZOOM_MAX = 200
const ZOOM_STEP = 25

const TOOL_PIX = 'pix'
const TOOL_LINHA = 'linha'

/**
 * Pré-visualização de PDF com zoom, páginas e captura de Pix / linha digitável.
 */
const PdfPreviewPanel = ({ url, label, onClose, onCapture }) => {
  const [zoom, setZoom] = useState(100)
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pdfFile, setPdfFile] = useState(null)
  const [pageWidth, setPageWidth] = useState(480)
  const [capturing, setCapturing] = useState(false)
  const [busyTool, setBusyTool] = useState(null)
  const [activeTool, setActiveTool] = useState(null)
  const [marquee, setMarquee] = useState(null)

  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
  }), [])
  const pdfRef = useRef(null)
  const viewportRef = useRef(null)
  const dragRef = useRef(null)

  useEffect(() => {
    setZoom(100)
    setPageNumber(1)
    setNumPages(null)
    setActiveTool(null)
    setMarquee(null)
    pdfRef.current = null
    setPdfFile(null)
  }, [url])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!url) {
        setPdfFile(null)
        return
      }
      if (String(url).startsWith('blob:') || String(url).startsWith('data:')) {
        setPdfFile(url)
        return
      }
      try {
        const res = await fetch(url)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const buf = await res.arrayBuffer()
        if (!cancelled) setPdfFile({ data: new Uint8Array(buf) })
      } catch (err) {
        if (!cancelled) setPdfFile(url)
      }
    }
    load()
    return () => { cancelled = true }
  }, [url])

  useEffect(() => {
    const el = viewportRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const apply = () => setPageWidth(Math.max(280, el.clientWidth - 24))
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(el)
    return () => ro.disconnect()
  }, [url, pdfFile])

  const onDocumentLoadSuccess = (pdf) => {
    pdfRef.current = pdf
    setNumPages(pdf.numPages)
    setPageNumber(1)
  }

  const finishCapture = useCallback((tool, value) => {
    if (!value) return false
    if (typeof onCapture === 'function') onCapture(value)
    notifySuccess(
      tool === TOOL_PIX
        ? 'Pix copia-e-cola capturado e preenchido no código de barras.'
        : 'Linha digitável capturada e preenchida no código de barras.',
    )
    setActiveTool(null)
    setMarquee(null)
    return true
  }, [onCapture])

  const parseFromText = useCallback((tool, text) => {
    if (!text) return null
    if (tool === TOOL_PIX) return extractPixCopiaECola(text)
    return extractLinhaDigitavel(text)
  }, [])

  const scanBarcodes = useCallback(async (tool, pdf) => {
    const formats = tool === TOOL_PIX
      ? ['qr_code']
      : ['itf', 'code_128', 'codabar']
    const values = []
    for (let n = 1; n <= pdf.numPages; n += 1) {
      const canvas = await renderPdfPageToCanvas(pdf, n, 2)
      const codes = await detectBarcodesFromCanvas(canvas, formats)
      values.push(...codes)
      if (tool === TOOL_PIX) {
        const pix = pickPixFromStrings(codes)
        if (pix) return pix
      } else {
        const linha = pickLinhaFromStrings(codes)
        if (linha) return linha
      }
    }
    return tool === TOOL_PIX
      ? pickPixFromStrings(values)
      : pickLinhaFromStrings(values)
  }, [])

  const scanDocument = useCallback(async (tool) => {
    const pdf = pdfRef.current
    if (!pdf) {
      notifyError('Aguarde o PDF carregar para capturar.')
      return
    }
    setCapturing(true)
    setBusyTool(tool)
    try {
      const text = await getPdfPlainText(pdf)
      const fromText = parseFromText(tool, text)
      if (fromText && finishCapture(tool, fromText)) return

      const fromBarcode = await scanBarcodes(tool, pdf)
      if (fromBarcode && finishCapture(tool, fromBarcode)) return

      setActiveTool(tool)
      notifyInfo(
        tool === TOOL_PIX
          ? 'Pix não encontrado automaticamente. Selecione o texto ou recorte a área no PDF.'
          : 'Linha digitável não encontrada automaticamente. Selecione o texto ou recorte a área no PDF.',
      )
    } catch (err) {
      console.error('Falha ao capturar do PDF', err)
      notifyError('Não foi possível ler este PDF. Tente selecionar o trecho manualmente.')
      setActiveTool(tool)
    } finally {
      setCapturing(false)
      setBusyTool(null)
    }
  }, [finishCapture, parseFromText, scanBarcodes])

  const applyFromUserSelection = useCallback((tool, region) => {
    const text = getSelectedOrRegionText(viewportRef.current, region)
    const value = parseFromText(tool, text)
    if (value) {
      finishCapture(tool, value)
      if (window.getSelection) window.getSelection().removeAllRanges()
      return true
    }
    return false
  }, [finishCapture, parseFromText])

  const handleToolClick = (tool) => {
    if (!url || capturing) return
    if (activeTool === tool) {
      scanDocument(tool)
      return
    }
    const selected = window.getSelection ? String(window.getSelection()) : ''
    if (selected.trim().length >= 8) {
      const value = parseFromText(tool, selected)
      if (value) {
        finishCapture(tool, value)
        window.getSelection().removeAllRanges()
        return
      }
    }
    scanDocument(tool)
  }

  const onPointerDown = (e) => {
    if (!activeTool || e.button !== 0) return
    if (e.target.closest && e.target.closest('.react-pdf__Page__textContent, .react-pdf__Page__textLayer')) {
      return
    }
    const vp = viewportRef.current
    if (!vp) return
    const r = vp.getBoundingClientRect()
    dragRef.current = {
      x: e.clientX - r.left + vp.scrollLeft,
      y: e.clientY - r.top + vp.scrollTop,
      clientX: e.clientX,
      clientY: e.clientY,
    }
    setMarquee({
      left: dragRef.current.x,
      top: dragRef.current.y,
      width: 0,
      height: 0,
    })
  }

  const onPointerMove = (e) => {
    if (!dragRef.current || !viewportRef.current) return
    const vp = viewportRef.current
    const r = vp.getBoundingClientRect()
    const x = e.clientX - r.left + vp.scrollLeft
    const y = e.clientY - r.top + vp.scrollTop
    const origin = dragRef.current
    setMarquee({
      left: Math.min(origin.x, x),
      top: Math.min(origin.y, y),
      width: Math.abs(x - origin.x),
      height: Math.abs(y - origin.y),
    })
  }

  const cropCanvasFromMarquee = (box) => {
    const vp = viewportRef.current
    if (!vp || !box || box.width < 8 || box.height < 8) return null
    const canvas = vp.querySelector('.react-pdf__Page canvas')
    if (!canvas) return null
    const pageEl = canvas.parentElement
    if (!pageEl) return null
    const pageRect = pageEl.getBoundingClientRect()
    const vpRect = vp.getBoundingClientRect()
    const absLeft = box.left - vp.scrollLeft + vpRect.left
    const absTop = box.top - vp.scrollTop + vpRect.top
    const cropLeft = absLeft - pageRect.left
    const cropTop = absTop - pageRect.top
    const scaleX = canvas.width / canvas.offsetWidth
    const scaleY = canvas.height / canvas.offsetHeight
    const sx = Math.max(0, cropLeft * scaleX)
    const sy = Math.max(0, cropTop * scaleY)
    const sw = Math.min(canvas.width - sx, box.width * scaleX)
    const sh = Math.min(canvas.height - sy, box.height * scaleY)
    if (sw < 4 || sh < 4) return null
    const out = document.createElement('canvas')
    out.width = sw
    out.height = sh
    out.getContext('2d').drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh)
    return out
  }

  const onPointerUp = async (e) => {
    if (!activeTool) return
    const origin = dragRef.current
    dragRef.current = null
    const box = marquee
    setMarquee(null)

    const dragged = origin && (
      Math.abs(e.clientX - origin.clientX) > 8
      || Math.abs(e.clientY - origin.clientY) > 8
    )

    if (dragged && box && box.width >= 8 && box.height >= 8) {
      const vp = viewportRef.current
      const vpRect = vp.getBoundingClientRect()
      const region = {
        left: box.left - vp.scrollLeft + vpRect.left,
        top: box.top - vp.scrollTop + vpRect.top,
        right: box.left - vp.scrollLeft + vpRect.left + box.width,
        bottom: box.top - vp.scrollTop + vpRect.top + box.height,
      }
      if (applyFromUserSelection(activeTool, region)) return
      setCapturing(true)
      setBusyTool(activeTool)
      try {
        const crop = cropCanvasFromMarquee(box)
        if (crop) {
          const formats = activeTool === TOOL_PIX
            ? ['qr_code']
            : ['itf', 'code_128', 'codabar']
          const codes = await detectBarcodesFromCanvas(crop, formats)
          const value = activeTool === TOOL_PIX
            ? pickPixFromStrings(codes)
            : pickLinhaFromStrings(codes)
          if (value && finishCapture(activeTool, value)) return
        }
        notifyError('Não foi possível ler essa área. Tente selecionar o texto ou recortar de novo.')
      } finally {
        setCapturing(false)
        setBusyTool(null)
      }
      return
    }

    window.setTimeout(() => {
      if (activeTool) applyFromUserSelection(activeTool, null)
    }, 0)
  }

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setActiveTool(null)
        setMarquee(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const toolDisabled = !url || capturing || !numPages

  return (
    <div className={`bordero-pdf-preview${activeTool ? ' is-capturing' : ''}`}>
      <div
        className="d-flex justify-content-between align-items-center px-2 bordero-pdf-preview__toolbar"
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label ? `Pré-visualização: ${label}` : 'Pré-visualização do PDF'}
        </span>

        {url && (
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 4 }}>
            <button
              type="button"
              className="btn btn-sm btn-light py-0 px-2"
              title="Diminuir zoom"
              disabled={zoom <= ZOOM_MIN}
              onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
            >
              −
            </button>
            <span style={{ minWidth: 44, textAlign: 'center', fontWeight: 600 }}>{zoom}%</span>
            <button
              type="button"
              className="btn btn-sm btn-light py-0 px-2"
              title="Aumentar zoom"
              disabled={zoom >= ZOOM_MAX}
              onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
            >
              +
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light py-0 px-2"
              title="Ajustar à largura"
              onClick={() => setZoom(100)}
            >
              Ajustar
            </button>

            <span style={{ width: 1, height: 16, background: '#cbd5e1', margin: '0 2px' }} />

            <button
              type="button"
              className="btn btn-sm btn-light py-0 px-2"
              title="Página anterior"
              disabled={!numPages || pageNumber <= 1}
              onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            >
              ‹
            </button>
            <span style={{ minWidth: 64, textAlign: 'center', fontWeight: 600 }}>
              {numPages ? `${pageNumber} / ${numPages}` : '—'}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-light py-0 px-2"
              title="Próxima página"
              disabled={!numPages || pageNumber >= numPages}
              onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
            >
              ›
            </button>

            <a
              className="btn btn-sm btn-light py-0 px-2"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title="Abrir em nova aba"
            >
              <i className="ri-external-link-line" />
            </a>

            {onClose && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ml-1"
                style={{ fontSize: 11 }}
                onClick={onClose}
              >
                Fechar
              </button>
            )}
          </div>
        )}
      </div>

      {url && (
        <div className="d-flex align-items-center px-2 py-1" style={{ gap: 6, borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
          <span style={{ fontSize: 11, color: '#64748b', marginRight: 4 }}>Ferramentas</span>
          <button
            type="button"
            className={`btn btn-sm py-0 px-2 bordero-pdf-preview__tool${activeTool === TOOL_PIX ? ' is-active' : ' btn-light'}`}
            title="Capturar Pix copia-e-cola do PDF"
            disabled={toolDisabled}
            onClick={() => handleToolClick(TOOL_PIX)}
          >
            <i className="ri-qr-code-line mr-1" />
            {busyTool === TOOL_PIX ? 'Lendo…' : 'Pix copia-e-cola'}
          </button>
          <button
            type="button"
            className={`btn btn-sm py-0 px-2 bordero-pdf-preview__tool${activeTool === TOOL_LINHA ? ' is-active' : ' btn-light'}`}
            title="Capturar linha digitável do PDF"
            disabled={toolDisabled}
            onClick={() => handleToolClick(TOOL_LINHA)}
          >
            <i className="ri-barcode-line mr-1" />
            {busyTool === TOOL_LINHA ? 'Lendo…' : 'Linha digitável'}
          </button>
        </div>
      )}

      {activeTool && (
        <div className="bordero-pdf-preview__hint">
          {activeTool === TOOL_PIX
            ? 'Modo captura Pix: selecione o código copia-e-cola ou recorte o QR no PDF. Esc cancela.'
            : 'Modo captura linha digitável: selecione a linha ou recorte o código de barras. Esc cancela.'}
        </div>
      )}

      {pdfFile ? (
        <div
          ref={viewportRef}
          className="bordero-pdf-preview__viewport"
          style={{ cursor: activeTool ? 'crosshair' : 'default' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          {marquee && marquee.width > 2 && (
            <div
              className="bordero-pdf-preview__marquee"
              style={{
                left: marquee.left,
                top: marquee.top,
                width: marquee.width,
                height: marquee.height,
              }}
            />
          )}
          <Document
            file={pdfFile}
            loading={
              <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 200, fontSize: 13 }}>
                Carregando PDF…
              </div>
            }
            error={
              <div className="d-flex flex-column align-items-center justify-content-center text-danger p-3" style={{ minHeight: 200, fontSize: 13, gap: 8 }}>
                <span>Falha ao carregar o PDF neste leitor.</span>
                <iframe
                  title="preview-pdf-fallback"
                  src={url}
                  style={{ width: '100%', height: 280, border: 0, background: '#fff' }}
                />
              </div>
            }
            onLoadSuccess={onDocumentLoadSuccess}
            options={pdfOptions}
          >
            <Page
              key={pageNumber}
              pageNumber={pageNumber}
              width={Math.round(pageWidth * (zoom / 100))}
              renderTextLayer
              renderAnnotationLayer={false}
            />
          </Document>
        </div>
      ) : (
        <div
          className="d-flex align-items-center justify-content-center text-muted"
          style={{ flex: 1, fontSize: 13 }}
        >
          Selecione ou anexe um PDF para visualizar aqui
        </div>
      )}
    </div>
  )
}

export default PdfPreviewPanel
